import {
  ArrowRightAltOutlined,
  ChevronRightOutlined,
  RouteOutlined,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardActionArea,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import React, { memo, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getPolicyCurrentValue,
  isFixedRoutePolicy,
} from "../../../pages/admin/route-policies/utils";
import { selectAccountCurrency } from "../../../store/features/accountSlice";
import {
  getRoutePolicies,
  selectRoutePoliciesByRole,
} from "../../../store/features/configs/routePoliciesSlice";
import formatAmount from "../../../utils/formatAmount";
import { curr } from "../../../utils/more/currency_country";

function FixedRoutesSelector({ role = "personal", onSelect = () => {} }) {
  const dispatch = useDispatch();
  const currency = useSelector(selectAccountCurrency);
  const { data = [], status } = useSelector((state) =>
    selectRoutePoliciesByRole(state, role),
  );

  useEffect(() => {
    if (status === "idle" || status === "failed") {
      dispatch(getRoutePolicies(role));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const fixedRoutes = useMemo(
    () =>
      (data || []).filter(
        (policy) => isFixedRoutePolicy(policy) && policy?.active !== false,
      ),
    [data],
  );

  if (status === "loading" && !data?.length) {
    return (
      <Box
        height={200}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <CircularProgress size={28} thickness={5} />
      </Box>
    );
  }

  if (!fixedRoutes.length) return null;

  return (
    <Box px={2}>
      <Typography
        textAlign={"center"}
        fontSize={".95rem"}
        fontWeight={"600"}
        mb={2}
        color="text.primary"
      >
        Rotas salvas
      </Typography>

      <Stack gap={1}>
        {fixedRoutes.map((policy) => {
          const point1 = policy?.preDefinedRoute?.point1 || "—";
          const point2 = policy?.preDefinedRoute?.point2 || "—";
          const value = getPolicyCurrentValue(policy);
          return (
            <Card
              key={policy?._id}
              variant="outlined"
              sx={{
                borderRadius: 2,
                transition: "all .15s",
                borderColor: "divider",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: 1,
                },
              }}
            >
              <CardActionArea
                onClick={() => onSelect(policy)}
                sx={{ p: 1.2, pr: 1 }}
              >
                <Stack direction="row" alignItems={"center"} gap={1.2}>
                  <RouteOutlined color="primary" sx={{ fontSize: "1.4rem" }} />
                  <Box flex={1} minWidth={0}>
                    <Typography
                      fontWeight={"600"}
                      fontSize={".92rem"}
                      noWrap
                      color="text.primary"
                    >
                      {policy?.name}
                    </Typography>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      gap={0.5}
                      sx={{ minWidth: 0 }}
                    >
                      <Typography
                        fontSize={".8rem"}
                        color="text.secondary"
                        noWrap
                        sx={{ maxWidth: "45%" }}
                      >
                        {point1}
                      </Typography>
                      <ArrowRightAltOutlined
                        sx={{ fontSize: "1rem", color: "text.secondary" }}
                      />
                      <Typography
                        fontSize={".8rem"}
                        color="text.secondary"
                        noWrap
                        sx={{ maxWidth: "45%" }}
                      >
                        {point2}
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack alignItems={"flex-end"}>
                    <Typography
                      fontWeight={"700"}
                      fontSize={".95rem"}
                      color="primary.main"
                      lineHeight={1.1}
                    >
                      {curr(currency)} {formatAmount(value)}
                    </Typography>
                  </Stack>
                  <ChevronRightOutlined
                    sx={{ color: "text.secondary", fontSize: "1.3rem" }}
                  />
                </Stack>
              </CardActionArea>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}

export default memo(FixedRoutesSelector);
